import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dadqanjep053ijub13u0';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function Newsletter() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // honeypot check
    const honey = String(data.get('website_alt') || '').trim();
    if (honey) {
      setStatus('success');
      setMessage('订阅成功，欢迎加入瓜田晚报！');
      form.reset();
      return;
    }

    // build urlencoded payload from actual form fields
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== 'website_alt') payload.append(key, String(value));
    });

    setStatus('loading');
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        text ||
        '';
      if (res.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg)) {
        setStatus('success');
        setMessage('订阅成功，最新鲜的瓜田速递会第一时间送到你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(/spam/i.test(serverMsg) ? '提交过于频繁，请稍后再试。' : (serverMsg || '提交失败，请稍后重试。'));
      }
    } catch {
      setStatus('error');
      setMessage('网络似乎开小差了，请稍后重试。');
    }
  };

  return (
    <section id="newsletter" className="w-full scroll-mt-24 bg-background-100 pb-12 md:pb-16">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 px-6 py-10 md:px-12 md:py-14">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-background-50/10" />
          <div className="pointer-events-none absolute -bottom-24 -left-10 h-64 w-64 rounded-full bg-secondary-500/20" />
          <div className="relative grid items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent-200">
                <i className="ri-news-line" aria-hidden="true" /> NEWSLETTER
              </span>
              <h2 className="mt-2 font-display text-2xl font-black text-background-50 md:text-3xl">订阅瓜田晚报，每天不迷路</h2>
              <p className="mt-3 max-w-lg text-sm leading-7 text-background-50/85">
                每天一次，把当日最值得围观的热点实况、反转与深度解析打包送到你邮箱。不刷屏、不废话，只发真正值得吃的瓜。
              </p>
              <ul className="mt-5 space-y-2 text-sm text-background-50/90">
                <li className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-accent-200" aria-hidden="true" /> 每日精选 3–5 条瓜田速递</li>
                <li className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-accent-200" aria-hidden="true" /> 独家实况还原与深度解析抢先看</li>
                <li className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-accent-200" aria-hidden="true" /> 可随时退订，绝无垃圾信息</li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-3">
              <div className="field-guard-box" aria-hidden="true">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
              </div>
              <label htmlFor="nl-email" className="text-xs text-background-50/80">你的邮箱地址</label>
              <input
                id="nl-email"
                name="email"
                type="email"
                required
                placeholder="name@example.com"
                className="w-full rounded-xl border border-background-50/25 bg-background-50/95 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-300"
              />
              <button type="submit" disabled={status === 'loading'} className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-70">
                {status === 'loading' ? <><i className="animate-spin ri-loader-4-line" aria-hidden="true" /> 正在订阅…</> : <><i className="ri-send-plane-fill" aria-hidden="true" /> 立即订阅</>}
              </button>
              {status === 'success' && <p className="flex items-center gap-1.5 text-sm text-accent-100"><i className="ri-checkbox-circle-fill" aria-hidden="true" /> {message}</p>}
              {status === 'error' && <p className="flex items-center gap-1.5 text-sm text-foreground-50"><i className="ri-alert-fill" aria-hidden="true" /> {message}</p>}
              <p className="text-xs text-background-50/70">点击订阅即表示你同意接收我们发送的邮件，我们承诺绝不将你的信息分享给任何第三方。</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}